import { createTheme } from '@mui/material/styles';

// Modern color palette inspired by Modernize theme
const colors = {
  primary: {
    main: '#5D87FF',
    light: '#ECF2FF',
    dark: '#4570EA',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#49BEFF',
    light: '#E8F7FF',
    dark: '#23AFDB',
    contrastText: '#ffffff',
  },
  success: {
    main: '#13DEB9',
    light: '#E6FFFA',
    dark: '#02B3A9',
    contrastText: '#ffffff',
  },
  error: {
    main: '#FA896B',
    light: '#FDEDE8',
    dark: '#F3704D',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#FFAE1F',
    light: '#FEF5E5',
    dark: '#F39711',
    contrastText: '#ffffff',
  },
  info: {
    main: '#539BFF',
    light: '#EBF3FE',
    dark: '#1682FF',
    contrastText: '#ffffff',
  },
  grey: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },
  text: {
    primary: '#2A3547',
    secondary: '#5A6A85',
    disabled: '#A8B0B9',
  },
  background: {
    default: '#F6F6F9',
    paper: '#ffffff',
  },
  divider: '#E5EAEF',
};

export const theme = createTheme({
  palette: colors,
  typography: {
    fontFamily: '"Plus Jakarta Sans", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.25rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '1.875rem',
      fontWeight: 700,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 700,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.57,
    },
    body2: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.66,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
    subtitle1: {
      fontSize: '0.875rem',
      fontWeight: 500,
    },
    subtitle2: {
      fontSize: '0.75rem',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 1px 3px rgba(0, 0, 0, 0.05)',
    '0px 2px 6px rgba(0, 0, 0, 0.05)',
    '0px 3px 12px rgba(0, 0, 0, 0.08)',
    '0px 4px 16px rgba(0, 0, 0, 0.08)',
    '0px 6px 20px rgba(0, 0, 0, 0.1)',
    '0px 8px 24px rgba(0, 0, 0, 0.12)',
    '0px 12px 28px rgba(0, 0, 0, 0.12)',
    '0px 16px 32px rgba(0, 0, 0, 0.12)',
    '0px 20px 36px rgba(0, 0, 0, 0.14)',
    '0px 24px 40px rgba(0, 0, 0, 0.14)',
    '0px 28px 44px rgba(0, 0, 0, 0.14)',
    '0px 32px 48px rgba(0, 0, 0, 0.16)',
    '0px 36px 52px rgba(0, 0, 0, 0.16)',
    '0px 40px 56px rgba(0, 0, 0, 0.16)',
    '0px 44px 60px rgba(0, 0, 0, 0.18)',
    '0px 48px 64px rgba(0, 0, 0, 0.18)',
    '0px 52px 68px rgba(0, 0, 0, 0.18)',
    '0px 56px 72px rgba(0, 0, 0, 0.2)',
    '0px 60px 76px rgba(0, 0, 0, 0.2)',
    '0px 64px 80px rgba(0, 0, 0, 0.2)',
    '0px 68px 84px rgba(0, 0, 0, 0.22)',
    '0px 72px 88px rgba(0, 0, 0, 0.22)',
    '0px 76px 92px rgba(0, 0, 0, 0.22)',
    '0px 80px 96px rgba(0, 0, 0, 0.24)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: 'thin',
          scrollbarColor: '#D1D5DB #F3F4F6',
          '&::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            background: '#F3F4F6',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#D1D5DB',
            borderRadius: 4,
            '&:hover': {
              background: '#9CA3AF',
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          padding: '8px 20px',
          fontSize: '0.875rem',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(93, 135, 255, 0.3)',
          },
        },
        outlined: {
          borderWidth: 1.5,
          '&:hover': {
            borderWidth: 1.5,
          },
        },
        sizeLarge: {
          padding: '12px 28px',
          fontSize: '0.9375rem',
        },
        sizeSmall: {
          padding: '6px 16px',
          fontSize: '0.8125rem',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.05)',
          border: '1px solid #E5EAEF',
          '&:hover': {
            boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.08)',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 24,
          '&:last-child': {
            paddingBottom: 24,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.05)',
        },
        elevation2: {
          boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          fontSize: '0.75rem',
          height: 24,
        },
        filled: {
          '&.MuiChip-colorPrimary': {
            backgroundColor: colors.primary.light,
            color: colors.primary.main,
          },
          '&.MuiChip-colorSecondary': {
            backgroundColor: colors.secondary.light,
            color: colors.secondary.main,
          },
          '&.MuiChip-colorSuccess': {
            backgroundColor: colors.success.light,
            color: colors.success.main,
          },
          '&.MuiChip-colorError': {
            backgroundColor: colors.error.light,
            color: colors.error.main,
          },
          '&.MuiChip-colorWarning': {
            backgroundColor: colors.warning.light,
            color: colors.warning.main,
          },
          '&.MuiChip-colorInfo': {
            backgroundColor: colors.info.light,
            color: colors.info.main,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          borderRight: '1px solid #E5EAEF',
          boxShadow: 'none',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          marginBottom: 4,
          '&.Mui-selected': {
            backgroundColor: colors.primary.light,
            color: colors.primary.main,
            '& .MuiListItemIcon-root': {
              color: colors.primary.main,
            },
            '&:hover': {
              backgroundColor: colors.primary.light,
            },
          },
          '&:hover': {
            backgroundColor: colors.grey[100],
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: colors.grey[50],
          '& .MuiTableCell-head': {
            fontWeight: 600,
            fontSize: '0.875rem',
            color: colors.text.primary,
            borderBottom: `1px solid ${colors.divider}`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${colors.divider}`,
          padding: '16px',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.05)',
        },
      },
    },
  },
});
